// Global
import React from 'react';

// Local
import { CardListing } from '@generated/CardListing/ContentGrid.model';
import { ContentGrid, ContentGridCardAlignment } from './ContentGrid';
import { ComponentParams, GetComponentServerProps, Item } from '@sitecore-content-sdk/nextjs';
import { Themes } from 'lib/themes';
import { usePreambleFields } from 'components/helpers/Molecules/Preamble/Preamble.sitecore';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { CardFields, fieldTypes } from '../CardListingShared';

// Data fetching
import {
  ContentListWithOverrides,
  DEFAULT_CARD_FALLBACK_ASPECT_RATIO,
  getContentListWithOverrides,
} from 'lib/utils/components/content-list-data';
import { PreambleOrientation } from 'components/helpers/Molecules/Preamble/Preamble';
import { ContentQueryProvider } from './ContentQueryContext';
import { RenderingParamBoolean } from 'lib/types/sitecore';
import { buildContentGridContentResultAnalyticsConfig } from 'components/helpers/Analytics/ContentGrid/ContentGridAnalytics';
import { useDefaultCardDetailLevel } from 'lib/hooks/useSiteSettings';

export type ContentGridParams = ComponentParams & {
  cardDetails: {
    fieldsToShow: Item[];
  };
  gridAspectRatio: Item;
  numColumns?: '1' | '2' | '3' | '4';
  centerLastRow?: RenderingParamBoolean;
  constrainedComponentWidth?: RenderingParamBoolean;
  useFavoritesContent?: RenderingParamBoolean;
  showManageFavorites?: RenderingParamBoolean;
  showLabel?: string | undefined;
};
type ContentGridProps = Omit<CardListing.ContentGrid.ContentGrid_Component, 'params'> & {
  staticProps: ContentListWithOverrides | null;
  params?: ContentGridParams;
};

const ContentGridContent = (props: ContentGridProps) => {
  // Get the default card detail level from site settings (handles both flat and nested shapes)
  const defaultCardDetailLevel = useDefaultCardDetailLevel();

  // Priority: 1) cardDetails from component params, 2) defaultCardDetailLevel from site settings, 3) all fields
  const fieldsToShow = props.params?.cardDetails?.fieldsToShow?.length
    ? props.params.cardDetails.fieldsToShow.map((x: Item) => x.name as CardFields)
    : defaultCardDetailLevel?.length
      ? defaultCardDetailLevel.map((x) => x.name as CardFields)
      : [...fieldTypes];

  const isEditing = useIsEditing();
  const preambleFields = usePreambleFields({
    fields: props.fields,
    ctaComponentClassOverrides: {
      cta1: 'component-content-grid-color-button-color-1',
      cta2: 'component-content-grid-color-button-color-2',
    },
  });

  const gridAspectRatio = getEnumValue<ImageAspectRatio>(props.params?.gridAspectRatio);
  const showManageFavorites = props.params?.showManageFavorites === '1';
  const showLabel = props.params?.showLabel === '1';
  const analyticsHeading = props.fields?.headline?.value?.trim() || undefined;
  const contentResultAnalytics = buildContentGridContentResultAnalyticsConfig(
    props.fields?.contentList,
    analyticsHeading
  );

  const result = (
    <ContentQueryProvider
      contentListItem={props.fields?.contentList}
      initialSearchResponse={props.staticProps?.result ?? null}
    >
      <ContentGrid
        theme={props.params?.theme as Themes}
        preambleOrientation={props.params?.preambleOrientation as PreambleOrientation}
        cardAlignment={props.params?.cardAlignment as ContentGridCardAlignment}
        gridAspectRatio={gridAspectRatio}
        {...preambleFields}
        contentOverrides={props.staticProps?.contentOverrides || {}}
        fallbackImageUrl={props.staticProps?.fallbackImageUrl}
        fieldsToShow={fieldsToShow}
        columns={props.params?.numColumns || '3'}
        centerLastRow={props.params?.centerLastRow === '1'}
        constrainedComponentWidth={props.params?.constrainedComponentWidth === '1'}
        showManageFavorites={showManageFavorites}
        showLabel={showLabel}
        contentResultAnalytics={contentResultAnalytics}
        excludeContentGridH2TitleFromTableOfContents={
          props.fields?.excludeContentGridH2TitleFromTableOfContents?.value === true
        }
      />
    </ContentQueryProvider>
  );
  if (isEditing) {
    const hiddenFields = fieldTypes.filter((field) => !fieldsToShow.includes(field));
    const hasNoContentDataSource = !props.fields?.contentList;
    const hasNoResults =
      !hasNoContentDataSource &&
      (!props.staticProps?.result || props.staticProps.result.Count === 0);

    // Determine the source of card details configuration
    const cardDetailsSource = props.params?.cardDetails?.fieldsToShow?.length
      ? 'Card Details (Component Level)'
      : defaultCardDetailLevel?.length
        ? 'Default Card Detail Level (Site Settings)'
        : 'All Fields (Default Fallback)';

    return (
      <div>
        <EditingHelpText>Card configuration source: {cardDetailsSource}</EditingHelpText>
        <EditingHelpText>Shown fields: {fieldsToShow.join(', ')}</EditingHelpText>
        <EditingHelpText>Hidden fields: {hiddenFields.join(', ')}</EditingHelpText>
        <EditingHelpText hideIf={!hasNoContentDataSource}>
          Content Grid requires a configured content data source with available results to render.
        </EditingHelpText>
        <EditingHelpText hideIf={!hasNoResults}>
          No items were found in the selected data source.
        </EditingHelpText>
        {result}
      </div>
    );
  }
  return result;
};

export const Default = (props: ContentGridProps) => {
  return <ContentGridContent {...props} />;
};

export const getComponentServerProps: GetComponentServerProps = async (rendering, layoutData) => {
  const params = rendering.params as ContentGridParams;
  return {
    staticProps: await getContentListWithOverrides(rendering, layoutData, {
      gridAspectRatio:
        getEnumValue<ImageAspectRatio>(params.gridAspectRatio) ??
        DEFAULT_CARD_FALLBACK_ASPECT_RATIO,
    }),
  };
};
